(ns practitest.frontend.tests.test-versioning-management-tab
  (:require
   [ajax.core :as ajax]
   [day8.re-frame.http-fx]
   [practitest.frontend.common-entities.events :as common-events]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.common.i18n :refer [get-translation format-translation]]
   [practitest.frontend.common.re-frame :refer [partial-dispatch]]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.components.modal :as modal]
   [practitest.frontend.components.notifications :as notifications]
   [practitest.frontend.components.grid.localstorage :as grid-storage]
   [practitest.frontend.components.grid.subs :as grid.subs]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.tooltip :as tooltip]
   [practitest.frontend.components2.grid :as grid2]
   [practitest.frontend.events :as events]
   [practitest.frontend.forms3.entity-form.header :as header]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.subs :as subs]
   [practitest.frontend.tests.version-label-common :as label-common]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]))

(re-frame/reg-event-fx
  ::remove-label-from-test
  (fn [{:keys [db]} [_ project-id test-id label-id]]
    {:db (update db :progress conj ::remove-label-from-test)
     :http-xhrio
     {:method          :post
      :uri             (broutes/get-url "qtest_designs#remove_label"
                                        {:p_id project-id
                                         :id test-id})
      :params          {:label_id label-id}
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::label-removed project-id test-id]
      :on-failure      [::label-remove-error]}}))

(re-frame/reg-event-fx
  ::label-removed
  (fn [{:keys [db]} [_ project-id test-id]]
    {:db (update db :progress disj ::remove-label-from-test)
     :dispatch-n [[::label-common/fetch-version-labels project-id test-id]
                  [::notifications/success2 (get-translation "test.versioning.remove-label.success" "Label successfully removed from test")]
                  [::events/fetch-history]]}))

(re-frame/reg-event-fx
  ::label-remove-error
  (fn [{:keys [db]} [_ error]]
    (let [error-message (label-common/extract-error-message error (get-translation "test.versioning.remove-label.error" "Failed to remove label from test"))]
      {:db (update db :progress disj ::remove-label-from-test)
       :dispatch [::notifications/error2 error-message {:auto-close true}]})))

(re-frame/reg-sub
  ::version-labels
  (fn [db]
    (get-in db [:entity :test :version-labels :data] [])))

(re-frame/reg-sub
  ::loading?
  (fn [db]
    (contains? (:progress db) ::label-common/fetch-version-labels)))

(re-frame/reg-sub
  ::current-test-id
  (fn [db]
    (get-in db [:entity :test :current :id])))

(re-frame/reg-sub
  ::has-call-to-test?
  (fn [db]
    (get-in db [:entity :test :current :has_call_to_test?])))

(defn version-label-view-button [_columns row]
  (let [test-id (:test_id row)
        current-test @(re-frame/subscribe [::subs/entity :test])
        current-test-id (:id (:current current-test))
        is-current? (= test-id current-test-id)]
    (if is-current?
      [tooltip/mux-tooltip
       {}
       (get-translation "test.versioning.view-current-version"
                        "This test version is the latest version you are currently using.")
       (fn [props]
         [:span.version-eye-disabled props
          [icons/mux-eye]])]
      [:span.clickable
       {:on-click (fn [e]
                    (.preventDefault e)
                    (when test-id
                      (let [url (broutes/get-url-view "qtest_designs#edit" {:id test-id})]
                           (dom/navigate! url))))}
       [icons/mux-eye]])))

(defn version-label-remove-button [on-remove-click can-create-labels? row]
  (if can-create-labels?
    [:span.clickable
     {:on-click (fn [e]
                 (.preventDefault e)
                 (on-remove-click row))}
     [icons/mux-trash]]
    [tooltip/mux-tooltip
     {}
     (get-translation "test.versioning.remove-label.no-permission"
                      "You don't have permission to remove version labels")
     (fn [props]
       [:span.version-remove-disabled props
        [icons/mux-trash]])]))

(defn version-labels-panel [_]
  (let [show-remove-modal? (reagent/atom false)
        label-to-remove (reagent/atom nil)]
    (fn [{:keys [form-id
                 entity-type]
          :as form-props}]
      (let [labels @(re-frame/subscribe [::version-labels])
            loading? @(re-frame/subscribe [::loading?])
            test-id @(re-frame/subscribe [::current-test-id])
            project-id @(re-frame/subscribe [::subs/current-project-id])
            has-call-to-test? @(re-frame/subscribe [::has-call-to-test?])
            can-create-labels?  @(re-frame/subscribe [::subs/versioning-can-create-labels?])
            table-id (str project-id "-" form-id "-version-labels-grid")
            on-remove-click (fn [label]
                              (reset! label-to-remove label)
                              (reset! show-remove-modal? true))
            on-confirm-remove (fn []
                                (reset! show-remove-modal? false)
                                (re-frame/dispatch [::remove-label-from-test
                                                    project-id
                                                    test-id
                                                    (:id @label-to-remove)]))
            on-cancel-remove (fn []
                               (reset! show-remove-modal? false)
                               (reset! label-to-remove nil))
               columns [{:type :text
                         :field :name
                         :header (get-translation "test.versioning.version-label" "Version Label")
                         :default-width 100
                         :systemcol? true
                         :sortable? true
                         :autofilter? true}
                        {:type :date
                         :field :added_to_test_at
                         :header (get-translation "generic.label.created-at" "Created at")
                         :default-width 120
                         :systemcol? true
                         :sortable? true
                         :autofilter? true}
                        {:type :text
                         :field :added_to_test_by
                         :header (get-translation "generic.label.created-by" "Created by")
                         :default-width 150
                         :systemcol? true
                         :sortable? true
                         :autofilter? true}
                        {:type :custom
                         :field :view
                         :header (get-translation "generic.label.view" "View")
                         :default-width 60
                         :systemcol? true
                         :sortable? false
                         :cell-fn version-label-view-button}
                        {:type :custom
                         :field :remove
                         :header (get-translation "test.versioning.remove-label.title" "Remove Label")
                         :default-width 60
                         :systemcol? true
                         :sortable? false
                         :cell-fn (fn [_ row] (version-label-remove-button on-remove-click can-create-labels? row))}]]
           [:div.instances-container
            [modal/bootstrap-modal
             {:is-open? @show-remove-modal?
              :on-request-close on-cancel-remove
              :close-button? true
              :centered? true
              :class "remove-label-modal"}

             [:span (get-translation "test.versioning.remove-label.title" "Remove Label")]

             [:div.remove-label-modal-body
              [:p (format-translation
                   (get-translation "test.versioning.remove-label.description-line1"
                                    "You chose to remove the \"%{label}\" label.")
                   :label (:name @label-to-remove))]
              [:p (get-translation "test.versioning.remove-label.description-line2"
                                   "This version will no longer be trackable or runnable, and past runs will stay intact but cannot regain this label.")]
              [:p (get-translation "test.versioning.remove-label.description-line3"
                                   "You can use this label for future versions of the test.")]
              (when has-call-to-test?
                [:div.call-to-test-warning
                 [icons/mux-circle-warning]
                 [:span
                  (get-translation "test.versioning.remove-label.call-to-test-warning"
                                   "This test contains a call to test step. If the called test inherited this label, you'll need to remove it manually.")]])]

             [:div.modal-buttons
              [buttons/button
               {:on-click on-cancel-remove
                :text (get-translation "generic.button.cancel" "Cancel")}]
              [buttons/button
               {:on-click on-confirm-remove
                :primary? true
                :text (get-translation "generic.button.remove" "Remove")}]]]

            (if loading?
              [:div.loading-spinner
               (get-translation "generic.loading" "Loading...")]
              [grid2/grid {:data labels
                           :columns columns
                           :class "data-grid-version-labels"
                           :full-data? true
                           :resizable? true
                           :columns-widths-save (reagent/partial grid-storage/save-column-sizes table-id)
                           :columns-widths-load (reagent/partial grid-storage/load-column-sizes table-id)
                           :show-edit-columns? false
                           :on-expand-click (partial-dispatch [::common-events/toggle-expanded entity-type :version-labels])
                           :expanded? @(re-frame/subscribe [::grid.subs/expanded? entity-type :version-labels])
                           :grid-toolbar (fn [grid-props]
                                           [buttons/button
                                            {:class "view-history-button"
                                             :primary? true
                                             :icon [icons/mux-project-history]
                                             :tertiary? true
                                             :text (get-translation "test.versioning.view-history" "View History")
                                             :on-click (fn []
                                                         (header/set-active-tab form-props :history {:event_type "Label Version"}))}])

                           :empty-table-message [:div.empty-section-grid
                                                 [:label (get-translation "test.versioning.table.no-labels" "No version labels found")]]}])]))))

(defn build-version-labels-tab []
      {:tab-id :version-labels
       :tab-title
       [:span.text-nowrap
        (get-translation "test.versioning.labels-tab.title" "Version Management")]
       :tab-view version-labels-panel})
